// src/layouts/reports/index.js
// Liste des rapports + création d’un Custom Report (Budget vs Actuals)

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  IconButton,
  Menu,
  ListItemText,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate } from "react-router-dom";

// ★ On passe par les services pour garantir la normalisation en tableaux
import { listCustomReportsAll, listBudgets, toList } from "services/reports";
import api from "api/axios"; // gardé pour la création

const ActionMenu = ({ row }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  return (
    <div key={`actions-${row.original.id}`}>
      <IconButton onClick={handleOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate(`/reports/view/${row.original.id}`);
          }}
        >
          <ListItemText primary="View" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            navigate(`/reports/edit/${row.original.id}`);
          }}
        >
          <ListItemText primary="Update" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            navigate(`/reports/assign/${row.original.id}`);
          }}
        >
          <ListItemText primary="Assign" />
        </MenuItem>
      </Menu>
    </div>
  );
};

ActionMenu.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
};

const Reports = () => {
  const [reports, setReports] = useState([]); // ← toujours un tableau
  const [budgets, setBudgets] = useState([]); // ← toujours un tableau
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    selectedBudgetId: "",
  });

  const loadReports = async () => {
    setLoading(true);
    try {
      // Liste des rapports normalisée
      const list = await listCustomReportsAll();
      setReports(list);
    } catch (error) {
      console.error("Erreur lors du chargement des rapports :", error);
      setReports([]); // sécurité : toujours un tableau
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    // Budgets normalisés (services.reports renvoie toujours un tableau)
    listBudgets()
      .then((arr) => setBudgets(arr))
      .catch((err) => {
        console.error("Erreur chargement budgets:", err);
        setBudgets([]);
      });
  }, []);

  const handleChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCreateReport = async () => {
    const { name, description, selectedBudgetId } = formValues;

    try {
      // Création directe via API (format attendu par le back)
      await api.post("/quickbooks/custom-reports/", {
        name,
        description,
        report_type: "budget_vs_actuals",
        filters: { budget_id: selectedBudgetId },
      });
      setOpenDialog(false);
      setFormValues({ name: "", description: "", selectedBudgetId: "" });
      await loadReports();
    } catch (error) {
      console.error("Erreur lors de la création du rapport :", error);
    }
  };

  const ActionsCell = ({ row }) => <ActionMenu row={row} />;

  ActionsCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired,
    }).isRequired,
  };

  const columns = [
    { Header: "Report Name", accessor: "name", align: "left" },
    { Header: "Description", accessor: "description", align: "left" },
    {
      Header: "Created At",
      accessor: "created_at",
      align: "left",
      Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      Cell: ActionsCell,
    },
  ];

  // Sécurité ultime : DataTable recevra TOUJOURS un tableau
  const safeRows = Array.isArray(reports) ? reports : toList(reports);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container justifyContent="flex-end" mb={2} px={3}>
          <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
            Add Report
          </Button>
        </Grid>

        <MDBox pt={3}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DataTable table={{ columns, rows: safeRows }} noEndBorder />
          )}
        </MDBox>
      </MDBox>
      <Footer />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Custom Report</DialogTitle>
        <DialogContent>
          <TextField
            label="Report Name"
            name="name"
            value={formValues.name}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Description"
            name="description"
            value={formValues.description}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Budget</InputLabel>
            <Select
              name="selectedBudgetId"
              value={formValues.selectedBudgetId}
              onChange={handleChange}
              label="Budget"
            >
              {budgets.map((budget) => (
                <MenuItem
                  key={budget.Id ?? budget.id ?? budget.ID}
                  value={budget.Id ?? budget.id ?? budget.ID}
                >
                  {budget.Name ?? budget.name ?? "Budget"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateReport} color="primary" variant="contained">
            Save Report
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Reports;
